
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useBlogStats, BlogStatsData } from "@/hooks/useBlogStats";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
  Line,
  LineChart,
  ReferenceLine,
  Label as RechartsLabel,
} from "recharts";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { RefreshCw, BarChart3, PieChart, Calendar, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { motion } from "framer-motion";

// Custom colors for charts
const CATEGORY_COLORS = [
  "#8884d8", "#83a6ed", "#8dd1e1", "#82ca9d", "#a4de6c",
  "#d0ed57", "#ffc658", "#ff8042", "#ff6361", "#bc5090"
];

// Monthly bar chart gradient
const MONTHLY_BAR_GRADIENT = {
  id: "monthlyBarGradient",
  colors: ["#8884d8", "#6E59A5"]
};

// Custom tooltip component for monthly chart with improved visuals
const CustomMonthlyTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  
  // Format the date correctly from the label (which should be YYYY-MM)
  const dateParts = label.split('-');
  if (dateParts.length !== 2) return null; // Invalid format
  
  const year = parseInt(dateParts[0]);
  const month = parseInt(dateParts[1]) - 1; // JS months are 0-based
  
  // Create date and ensure it's valid
  const date = new Date(year, month);
  if (isNaN(date.getTime())) return null; // Invalid date
  
  const formattedDate = date.toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });
  
  // Calculate if this month has more or fewer posts than the previous month
  const currentValue = payload[0].value;
  const prevValue = payload[0].payload.prevCount || 0;
  const difference = currentValue - prevValue;
  
  return (
    <div className="bg-white p-3 border border-gray-200 rounded shadow-lg text-sm">
      <p className="font-medium text-base mb-1">{formattedDate}</p>
      <p className="text-gray-900 font-bold text-lg mb-1">{`${payload[0].value} posts`}</p>
      
      {payload[0].payload.hasPrevious && (
        <div className="flex items-center mt-1 text-xs">
          <span className="mr-1">vs previous month:</span>
          {difference > 0 ? (
            <span className="flex items-center text-green-600 font-medium">
              <TrendingUp size={14} className="mr-1" />
              +{difference} ({prevValue > 0 ? ((difference / prevValue) * 100).toFixed(0) : '∞'}%)
            </span>
          ) : difference < 0 ? (
            <span className="flex items-center text-red-600 font-medium">
              <TrendingDown size={14} className="mr-1" />
              {difference} ({prevValue > 0 ? ((difference / prevValue) * 100).toFixed(0) : '0'}%)
            </span>
          ) : (
            <span className="flex items-center text-gray-600 font-medium">
              <Minus size={14} className="mr-1" />
              No change
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Custom tooltip component for category chart
const CustomCategoryTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  
  return (
    <div className="bg-white p-3 border border-gray-200 rounded shadow-lg text-sm">
      <p className="font-medium text-base mb-1">{`${label}`}</p>
      <p className="text-gray-900 font-bold text-lg">{`${payload[0].value} posts`}</p>
      <p className="text-gray-500 text-xs mt-1">
        {`${((payload[0].value / payload[0].payload.total) * 100).toFixed(1)}% of all posts`}
      </p>
    </div>
  );
};

const BlogPostStats = () => {
  const queryClient = useQueryClient();
  const { data: stats, isLoading, error, refetch, isRefetching } = useBlogStats();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Handle refresh action
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ["blog-stats"] });
      await refetch();
      toast.success("Blog statistics refreshed");
    } catch (error) {
      console.error("Failed to refresh stats:", error);
      toast.error("Failed to refresh statistics");
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={index} className="bg-slate-50 border-slate-100">
            <CardContent className="pt-6">
              <div className="text-sm font-medium text-slate-600 mb-1">Loading...</div>
              <div className="h-8 w-12 bg-slate-200 animate-pulse rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md border border-red-200 text-red-800">
        <p className="font-medium">Failed to load blog statistics</p>
        <p className="text-sm mt-1 text-red-600">Please try refreshing or contact support if the issue persists.</p>
        <Button 
          variant="outline" 
          size="sm" 
          className="mt-2"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw size={14} className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh Stats'}
        </Button>
      </div>
    );
  }

  // Prepare and enhance chart data for monthly posts with month-to-month comparison
  const prepareMonthlyChartData = () => {
    if (!stats?.posts_by_month || stats.posts_by_month.length === 0) {
      return [];
    }
    
    // Sort chronologically first
    const sortedData = [...stats.posts_by_month].sort((a, b) => 
      new Date(a.month).getTime() - new Date(b.month).getTime()
    );
    
    // Add month-to-month comparison data
    return sortedData.map((item, index) => {
      // Parse the YYYY-MM format 
      const dateParts = item.month.split('-');
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // JS months are 0-based
      
      // Create a proper date object
      const date = new Date(year, month);
      
      // Format it for display
      const formattedMonth = date.toLocaleDateString('en-US', { 
        month: 'short', 
        year: '2-digit' 
      });
      
      const hasPrevious = index > 0;
      const prevCount = hasPrevious ? sortedData[index - 1].count : 0;
      
      return {
        name: item.month, // Keep original YYYY-MM format for data key
        displayName: formattedMonth,
        count: item.count,
        prevCount: prevCount,
        hasPrevious: hasPrevious,
        difference: item.count - prevCount,
        percentChange: hasPrevious ? ((item.count - prevCount) / prevCount) * 100 : 0
      };
    });
  };

  const chartData = prepareMonthlyChartData();

  // Calculate average posts per month for reference line
  const averagePostsPerMonth = chartData.length > 0 
    ? chartData.reduce((sum, item) => sum + item.count, 0) / chartData.length 
    : 0;

  // Prepare categories data with total for percentage calculation
  const totalCategoryPosts = stats?.categories?.reduce((sum, cat) => sum + cat.count, 0) || 0;
  const categoryData = stats?.categories?.map((item, index) => ({
    name: item.category,
    value: item.count,
    color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
    total: totalCategoryPosts
  })) || [];

  return (
    <div className="space-y-8">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0 }}
        >
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100 hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              <div className="text-sm font-medium text-blue-600 mb-1">Total Posts</div>
              <div className="text-3xl font-bold">{stats?.total_posts || 0}</div>
            </CardContent>
          </Card>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-100 hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              <div className="text-sm font-medium text-green-600 mb-1">Published</div>
              <div className="text-3xl font-bold">{stats?.published_posts || 0}</div>
            </CardContent>
          </Card>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-100 hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              <div className="text-sm font-medium text-amber-600 mb-1">Drafts</div>
              <div className="text-3xl font-bold">{stats?.draft_posts || 0}</div>
            </CardContent>
          </Card>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Card className="bg-gradient-to-br from-blue-50 to-sky-50 border-blue-100 hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              <div className="text-sm font-medium text-blue-600 mb-1">Scheduled</div>
              <div className="text-3xl font-bold">{stats?.scheduled_posts || 0}</div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend Chart */}
        {chartData.length > 0 ? (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-0">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar size={18} />
                    Posts by Month (Publication Timeline)
                  </CardTitle>
                  <CardDescription>
                    Monthly post comparison showing publishing trends over time
                  </CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleRefresh}
                  disabled={isRefreshing || isRefetching}
                >
                  <RefreshCw 
                    size={16} 
                    className={`${isRefreshing || isRefetching ? 'animate-spin' : ''}`} 
                  />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ErrorBoundary>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 0,
                        bottom: 5,
                      }}
                    >
                      <defs>
                        <linearGradient id="monthlyBarGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#8884d8" stopOpacity={0.9}/>
                          <stop offset="100%" stopColor="#6E59A5" stopOpacity={0.7}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.5} />
                      <XAxis 
                        dataKey="displayName" 
                        tickMargin={10}
                        tick={{ fontSize: 12 }}
                        axisLine={{ stroke: '#e5e7eb' }}
                      />
                      <YAxis 
                        allowDecimals={false}
                        domain={[0, 'dataMax + 1']} 
                        axisLine={{ stroke: '#e5e7eb' }}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `${value}`}
                      />
                      <Tooltip content={<CustomMonthlyTooltip />} />
                      <ReferenceLine y={averagePostsPerMonth} 
                        stroke="#FF8042" 
                        strokeDasharray="3 3"
                        strokeWidth={2}
                        isFront={false}
                        opacity={0.8}>
                        <RechartsLabel 
                          value="Average" 
                          position="right" 
                          fill="#FF8042"
                          fontSize={12}
                          offset={5}
                        />
                      </ReferenceLine>
                      
                      <Bar 
                        dataKey="count" 
                        name="Posts" 
                        fill="url(#monthlyBarGradient)" 
                        radius={[4, 4, 0, 0]}
                        animationDuration={1000}
                        animationBegin={200}
                      >
                        {chartData.map((entry, index) => {
                          return (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.difference > 0 ? 'url(#monthlyBarGradient)' : '#8884d8'} 
                              strokeWidth={0}
                            />
                          );
                        })}
                      </Bar>
                      
                      {/* Add a trend line connecting the tops of the bars */}
                      <Line 
                        type="monotone" 
                        dataKey="count" 
                        stroke="#ff7300" 
                        strokeWidth={2}
                        dot={{ r: 3, fill: "#ff7300", strokeWidth: 0 }}
                        activeDot={{ r: 5 }}
                        isAnimationActive={true}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ErrorBoundary>
            </CardContent>
          </Card>
        ) : (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle>Posts by Month (Publication Timeline)</CardTitle>
              <CardDescription>Monthly post comparison showing publishing trends over time</CardDescription>
            </CardHeader>
            <CardContent className="pt-0 text-center py-12">
              <p className="text-slate-500 mb-2">No post data available yet</p>
              <p className="text-slate-400 text-sm">Start creating blog posts to see statistics here</p>
            </CardContent>
          </Card>
        )}

        {/* Categories Chart */}
        {categoryData.length > 0 ? (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-0">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 size={18} />
                    Top Categories
                  </CardTitle>
                  <CardDescription>Distribution of post categories</CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleRefresh}
                  disabled={isRefreshing || isRefetching}
                >
                  <RefreshCw 
                    size={16} 
                    className={`${isRefreshing || isRefetching ? 'animate-spin' : ''}`} 
                  />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ErrorBoundary>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={categoryData}
                      layout="vertical"
                      margin={{
                        top: 20,
                        right: 30,
                        left: 60,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} opacity={0.5} />
                      <XAxis 
                        type="number" 
                        axisLine={{ stroke: '#e5e7eb' }}
                        tick={{ fontSize: 12 }}
                        tickCount={5}
                        allowDecimals={false}
                      />
                      <YAxis 
                        dataKey="name" 
                        type="category"
                        tick={{ fontSize: 12 }}
                        width={100}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickFormatter={(value) => 
                          value.length > 15 ? `${value.substring(0, 13)}...` : value
                        }
                      />
                      <Tooltip content={<CustomCategoryTooltip />} />
                      <Bar 
                        dataKey="value" 
                        name="Posts" 
                        radius={[0, 4, 4, 0]}
                        animationDuration={1500}
                        animationBegin={300}
                      >
                        {categoryData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.color} 
                            fillOpacity={0.9}
                            strokeWidth={0}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ErrorBoundary>
            </CardContent>
          </Card>
        ) : (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle>Top Categories</CardTitle>
              <CardDescription>Distribution of post categories</CardDescription>
            </CardHeader>
            <CardContent className="pt-0 text-center py-12">
              <p className="text-slate-500 mb-2">No category data available</p>
              <p className="text-slate-400 text-sm">Add categories to your posts using the metadata field</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BlogPostStats;
